import Layout from 'components/layout';
import { useEffect, useState } from 'react';
import ContentPage from 'components/content-pages';
import { handleContentPageServerSideProps } from 'utils/content-pages';
import styles from './style.module.scss';
import { buildClassName } from 'utils/build-class-name';
import CloseIconExpForm from 'components/icon/close-icon-exp-form';
import { TenantIds, TenantTypes } from '@tenants';
import { trackEvent } from 'utils/google-tag-manager';
import { GTM_VIEW_EXP_JOIN_PAGE } from 'constants/events';
import { createGetServerSideProps, HandlerType } from 'utils/next/shared-getServerSideProps';

import type { GetServerSidePropsContext } from 'next';
import type { ContentPageProps } from 'types/content-pages';
import type { FeaturesType } from 'contexts';

declare global {
  var hbspt: {
    forms: {
      create: (options: { portalId: string; formId: string; region: string; target?: string; locale?: string }) => void;
    };
  };
}

const HUBSPOT_FORM_PORTAL_ID = '24210079';
const HUBSPOT_FORM_ID = '4580e06f-e40c-4785-b65c-6e143069f3b3';

// URL patterns to be replaced with our internal contact page
const URL_REPLACEMENTS = [
  {
    // Match both href and target="_blank" attributes
    pattern: /href="https:\/\/joinapp\.exprealty\.com\/link\/us[^"]*"[^>]*target="_blank"/g,
    replacement: 'href="/join-exp-us-contact" target="_blank"',
  },
  {
    // Match both href and target="_blank" attributes
    pattern: /href="https:\/\/join\.exprealty\.com\/contact\/[^"]*"[^>]*target="_blank"/g,
    replacement: 'href="/join-exp-us-contact" target="_blank"',
  },
] as const;

const replaceExternalUrls = (content: string): string => {
  return URL_REPLACEMENTS.reduce(
    (processedContent, { pattern, replacement }) =>
      processedContent.replace(pattern, replacement),
    content
  );
};

const handler: HandlerType<ContentPageProps> = async (_context: GetServerSidePropsContext, _features?: FeaturesType, tenant?: TenantTypes) => {
  return await handleContentPageServerSideProps(tenant.id, [
    TenantIds.EXP_IN_SERVICE,
    TenantIds.EXP_REALTY_US,
  ], '/join-exp', 'ALL');
};

export const getServerSideProps = createGetServerSideProps(handler);

export default function JoinExpPage(props: ContentPageProps) {
  const processedProps = {
    ...props,
    siteContent: props.siteContent ? replaceExternalUrls(props.siteContent) : props.siteContent,
  };

  useEffect(() => {
    trackEvent(GTM_VIEW_EXP_JOIN_PAGE);
  }, []);

  // hasFormBeenOpened is set to true once the user opens the form for the first time, needed so that the fade out animation
  // doesn't play whenever a user visits the page for the first time. Same idea for hasButtonBeenShown
  const [hasFormBeenOpened, setHasFormBeenOpened] = useState(false);
  const [hasButtonBeenShown, setHasButtonBeenShown] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isFounderSectionVisible, setIsFounderSectionVisible] = useState(false);
  const [isBottomFounderSectionVisible, setIsBottomFounderSectionVisible] = useState(false);
  const [hasScrolledUpPastFounderSection, setHasScrolledUpPastFounderSection] = useState(true);
  const [showOpenFormButton, setShowOpenFormButton] = useState(false);

  const onCloseClick = () => {
    setIsFormOpen(false);
  };

  const onLearnMoreClick = () => {
    setIsFormOpen(!isFormOpen);
    if (!hasFormBeenOpened) {
      setHasFormBeenOpened(true);
    }
  };

  useEffect(() => {
    const topObserver = new IntersectionObserver(entries => {
      const founderSection = entries[0];
      setIsFounderSectionVisible(founderSection.isIntersecting);
    });

    const bottomObserver = new IntersectionObserver(entries => {
      const founderSectionBottom = entries[0];
      setIsBottomFounderSectionVisible(founderSectionBottom.isIntersecting);
    });

    topObserver.observe(document.getElementById('founderSection'));
    bottomObserver.observe(document.getElementById('founderSectionBottom'));

  }, []);

  useEffect(() => {
    // Once the bottom of the founder section comes into view, show the Got Questions? button
    if (isBottomFounderSectionVisible) {
      setShowOpenFormButton(true);
      setHasScrolledUpPastFounderSection(false);
      if (!hasButtonBeenShown) {
        setHasButtonBeenShown(true);
      }
    }
    // If the top of the founder section is visible but not the bottom, this means the user has scrolled
    // up, so we hide the button
    else if (isFounderSectionVisible && !isBottomFounderSectionVisible) {
      setHasScrolledUpPastFounderSection(true);
    }

    if (hasScrolledUpPastFounderSection && !isFormOpen) {
      setShowOpenFormButton(false);
    }
  }, [hasButtonBeenShown, hasScrolledUpPastFounderSection, isBottomFounderSectionVisible, isFormOpen, isFounderSectionVisible]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src='//js.hsforms.net/forms/embed/v2.js';
    document.body.appendChild(script);
    script.addEventListener('load', () => {
      if (window.hbspt) {
        window.hbspt.forms.create({
          portalId: HUBSPOT_FORM_PORTAL_ID,
          formId: HUBSPOT_FORM_ID,
          target: '#learnMoreForm',
          region: 'na1',
        });
      }
    });
  }, []);

  return (
    <Layout>
      <div className={buildClassName(styles['form-component'], !hasFormBeenOpened ? '' : isFormOpen ? styles['open-form'] : styles['close-form'])}>
        <div className={styles.header}>
          <h4>Got Questions?</h4>
          <CloseIconExpForm className={styles.close} onClick={onCloseClick}/>
        </div>
        <div id='learnMoreForm' className={styles['learn-more-form']}>
        </div>
      </div>
      <ContentPage {...processedProps} />
      <div
        onClick={onLearnMoreClick}
        aria-label='Button'
        className={buildClassName(styles['learn-more-button'], !hasButtonBeenShown ? '' : showOpenFormButton ? styles['show-button'] : styles['hide-button'])}
      >
        {isFormOpen ? 'Close' : 'Got Questions?'}
      </div>
    </Layout>
  );
}